/**
 * Cmd-K / Ctrl-K global search palette.
 *
 * Mounted once at the app shell. Listens for the keyboard shortcut on
 * the document; opens a modal with a search input that hits
 * /api/search?q=... debounced 200ms. Results are grouped (Items /
 * Units / Jobs / Clients) and arrow keys + Enter navigate. Esc closes.
 *
 * Closed state is essentially free — no fetches happen until the user
 * opens it. Open state keeps focus management tight so it feels like a
 * native palette rather than a generic modal.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchApi, type SearchResults, type SearchHit } from '@/api/search';
import { getRecents, recordRecent, type RecentEntry } from '@/lib/recentlyViewed';

type FlatHit = SearchHit & { category: 'Recent' | 'Items' | 'Units' | 'Jobs' | 'Clients' };

const EMPTY_RESULTS: SearchResults = { items: [], units: [], jobs: [], clients: [] };

/** Opens the palette from anywhere (e.g. the top-bar search button). */
export function openCommandPalette() {
  window.dispatchEvent(new Event('open-command-palette'));
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [recents, setRecents] = useState<RecentEntry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Global shortcut: Cmd-K (mac) or Ctrl-K (everyone else) ─────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isPaletteCombo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isPaletteCombo) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    // Also openable via the top-bar search button (see openCommandPalette).
    function onOpenEvent() { setOpen(true); }
    document.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpenEvent);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onOpenEvent);
    };
  }, [open]);

  // Reset state each time the palette is opened, and focus the input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(EMPTY_RESULTS);
      setActiveIdx(0);
      setRecents(getRecents());
      // setTimeout so the autoFocus + focus combo doesn't race the portal
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // ── Debounced search ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length === 0) {
      setResults(EMPTY_RESULTS);
      setBusy(false);
      return;
    }
    setBusy(true);
    const handle = setTimeout(() => {
      searchApi.query(q)
        .then(setResults)
        .catch(() => setResults(EMPTY_RESULTS))
        .finally(() => setBusy(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [query, open]);

  // Flatten the grouped results for arrow-key navigation. With no query yet,
  // the list is the user's recently viewed pages instead.
  const flat: FlatHit[] = useMemo(() => {
    if (query.trim().length === 0) {
      return recents.map((r) => ({ ...r, category: 'Recent' as const }));
    }
    const out: FlatHit[] = [];
    for (const h of results.jobs)    out.push({ ...h, category: 'Jobs' });
    for (const h of results.units)   out.push({ ...h, category: 'Units' });
    for (const h of results.items)   out.push({ ...h, category: 'Items' });
    for (const h of results.clients) out.push({ ...h, category: 'Clients' });
    return out;
  }, [results, recents, query]);

  useEffect(() => { setActiveIdx(0); }, [flat.length, query]);

  function go(hit: SearchHit) {
    // Record here as well as on detail-page load, so anything opened via
    // search (units, clients, ...) shows up in Recent even when its page
    // doesn't record itself. Deduped by href in recordRecent.
    recordRecent({ label: hit.label, sublabel: hit.sublabel, href: hit.href });
    setOpen(false);
    navigate(hit.href);
  }

  function onInputKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[activeIdx];
      if (hit) go(hit);
    }
  }

  if (!open) return null;

  // Re-group from `flat` so the category order matches the keyboard
  // navigation order (Jobs first because that's what people search most).
  const grouped: Array<{ category: FlatHit['category']; hits: FlatHit[] }> = [];
  for (const h of flat) {
    const bucket = grouped.find((g) => g.category === h.category);
    if (bucket) bucket.hits.push(h);
    else grouped.push({ category: h.category, hits: [h] });
  }

  const hasQuery = query.trim().length > 0;
  const showEmptyState = hasQuery && !busy && flat.length === 0;

  return (
    <div style={overlayStyle} onClick={() => setOpen(false)}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Input */}
        <div style={{ position: 'relative', borderBottom: '1px solid #eee' }}>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search jobs, assets, items, clients..."
            style={{
              width: '100%', border: 'none', outline: 'none',
              fontSize: 16, padding: '16px 18px 16px 44px',
              background: 'transparent', boxSizing: 'border-box',
            }}
          />
          <span style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            color: '#999', fontSize: 16, pointerEvents: 'none',
          }}>⌕</span>
          {busy && (
            <span style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              color: '#999', fontSize: 11,
            }}>Searching...</span>
          )}
        </div>

        {/* Results */}
        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {!hasQuery && flat.length === 0 && (
            <p style={emptyHint}>Start typing — e.g. an asset ID, "J-1042", a client name.</p>
          )}
          {showEmptyState && (
            <p style={emptyHint}>No matches for "{query}".</p>
          )}
          {grouped.map((g, gi) => (
            <div key={g.category}>
              <div style={categoryHeader}>{g.category}</div>
              {g.hits.map((h, hi) => {
                const flatIdx = (() => {
                  // Compute the absolute index in the flat list (cheap, small lists).
                  let n = 0;
                  for (let i = 0; i < gi; i++) n += grouped[i].hits.length;
                  return n + hi;
                })();
                const isActive = flatIdx === activeIdx;
                return (
                  <button
                    key={h.assetId ?? h.id ?? `${g.category}-${hi}`}
                    onClick={() => go(h)}
                    onMouseEnter={() => setActiveIdx(flatIdx)}
                    style={{
                      ...resultRow,
                      background: isActive ? '#f4f3ff' : 'transparent',
                      borderLeft: `3px solid ${isActive ? 'var(--brand)' : 'transparent'}`,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#222' }}>{h.label}</span>
                    {h.sublabel && (
                      <span style={{ marginLeft: 10, color: '#888', fontSize: 12 }}>{h.sublabel}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <span style={kbd}>↑↓</span> navigate &nbsp;
          <span style={kbd}>↵</span> open &nbsp;
          <span style={kbd}>esc</span> close
          <span style={{ float: 'right' }}>
            <span style={kbd}>{/Mac/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</span> opens this anywhere
          </span>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  paddingTop: '12vh',
  zIndex: 1000,
};
const panelStyle: React.CSSProperties = {
  width: '100%', maxWidth: 560,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
  overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
};
const categoryHeader: React.CSSProperties = {
  padding: '8px 18px 4px',
  fontSize: 10, fontWeight: 700, color: '#999',
  textTransform: 'uppercase', letterSpacing: 1,
};
const resultRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center',
  width: '100%',
  padding: '10px 18px',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 14,
  background: 'transparent',
};
const footerStyle: React.CSSProperties = {
  borderTop: '1px solid #eee',
  padding: '8px 18px',
  fontSize: 11,
  color: '#888',
  background: '#fafafa',
};
const kbd: React.CSSProperties = {
  display: 'inline-block',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: 4,
  padding: '0 5px',
  fontSize: 10,
  color: '#666',
};
const emptyHint: React.CSSProperties = {
  padding: '14px 18px', fontSize: 13, color: '#999', margin: 0,
};
